import { z } from 'zod';
import { GUILD_MAX_MEMBERS, GUILD_RANKS } from '../rules/guild.js';
import { CharacterPortraitSchema } from './party.js';
/**
 * Guilds (ADR 0036). A client founds one, invites by name, answers an
 * invitation, leaves, or — ranking high enough — sends a member away,
 * changes a rank or the motto. The server keeps the guilds and tells each
 * member the whole guild after every change.
 */
const CharacterIdSchema = z.string().min(1).max(64);
export const GUILD_NAME_MIN = 3;
export const GUILD_NAME_MAX = 24;
/** Letters, digits, spaces, apostrophes and hyphens inside. */
const GUILD_NAME_PATTERN = /^[\p{L}\p{N}]+(?:[ '’-][\p{L}\p{N}]+)*$/u;
export const GuildNameSchema = z
    .string()
    .trim()
    .min(GUILD_NAME_MIN, `Le nom fait au moins ${String(GUILD_NAME_MIN)} caractères`)
    .max(GUILD_NAME_MAX, `Le nom fait au plus ${String(GUILD_NAME_MAX)} caractères`)
    .regex(GUILD_NAME_PATTERN, 'Lettres et chiffres, séparés d’espaces, d’apostrophes ou de tirets');
/** Two to four capitals or digits, shown after the name. */
export const GuildTagSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{2,4}$/, 'Le tag fait 2 à 4 lettres ou chiffres');
export const GUILD_MOTD_MAX = 200;
export const GuildRankSchema = z.enum(GUILD_RANKS);
export const GuildCreateMessageSchema = z.object({ name: GuildNameSchema, tag: GuildTagSchema });
export const GuildInviteMessageSchema = z.object({ name: z.string().trim().min(1).max(32) });
/** Answer the invitation of guild `guildId` (as `guild_invited` gave it). */
export const GuildAnswerMessageSchema = z.object({ guildId: z.uuid(), accept: z.boolean() });
export const GuildLeaveMessageSchema = z.object({}).strict();
export const GuildDisbandMessageSchema = z.object({}).strict();
export const GuildKickMessageSchema = z.object({ characterId: CharacterIdSchema });
/** The leader sets a member's rank: officer or member, never a second leader. */
export const GuildRankMessageSchema = z.object({
    characterId: CharacterIdSchema,
    rank: z.enum(['officer', 'member']),
});
export const GuildMotdMessageSchema = z.object({
    text: z
        .string()
        .transform((text) => text
        .replace(/\s+/g, ' ')
        .replace(/[\p{Cc}\p{Cf}]/gu, '')
        .trim())
        .pipe(z.string().max(GUILD_MOTD_MAX)),
});
export const GuildMemberSchema = z.object({
    id: CharacterIdSchema,
    name: z.string().min(1),
    rank: GuildRankSchema,
    online: z.boolean(),
    /** In the game but away from the keyboard (ADR 0080). */
    away: z.boolean().default(false),
    /** Where they play, while online. */
    zoneId: z.string().min(1).nullable(),
    level: z.number().int().min(1),
    /** Their head as the zone shows them, while online; null away (like a friend's, ADR 0033). */
    portrait: CharacterPortraitSchema.nullable().default(null),
});
/** To every member after each change, and to someone who leaves: null for no guild. */
export const GuildStateSchema = z
    .object({
    id: z.uuid(),
    name: z.string().min(1),
    tag: z.string().min(2).max(4),
    motd: z.string().max(GUILD_MOTD_MAX),
    myRank: GuildRankSchema,
    members: z.array(GuildMemberSchema).max(GUILD_MAX_MEMBERS),
})
    .nullable();
/** To the invited player: which guild, who asks, how long the question stands. */
export const GuildInvitedSchema = z.object({
    guildId: z.uuid(),
    guildName: z.string().min(1),
    tag: z.string().min(2).max(4),
    from: z.string().min(1),
    expiresInMs: z.number().int().min(0),
});
export const GUILD_EVENT_KINDS = [
    'created',
    /** To the one who invited: the invitation is on its way. */
    'invited',
    'joined',
    'left',
    'kicked',
    'declined',
    'expired',
    'promoted',
    'demoted',
    'leader',
    'motd',
    'disbanded',
    /** To the other members: one of them reached a new level. */
    'levelled',
];
export const GuildEventSchema = z.object({
    kind: z.enum(GUILD_EVENT_KINDS),
    /** The player it happened to, or the guild for `created` and `disbanded`. */
    name: z.string().min(1),
    /** The level reached, on `levelled`. */
    level: z.number().int().min(1).optional(),
});
export const GUILD_REFUSALS = [
    'invalid',
    /** Nobody of that name is in the game. */
    'not-found',
    'self',
    /** The invited player already has a guild. */
    'already-in-guild',
    /** You already have one; leave it first. */
    'in-guild',
    'no-guild',
    /** Your rank does not allow it. */
    'not-allowed',
    'full',
    'pending',
    'no-invite',
    'not-member',
    'missing-gold',
    'name-taken',
    'tag-taken',
    /** The word filter (ADR 0037) refused the name or the tag. */
    'name-refused',
];
export const GuildRejectedSchema = z.object({ reason: z.enum(GUILD_REFUSALS) });
//# sourceMappingURL=guild.js.map